import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import colors from "@ugitgud/legos/plugins/colors.json";

export const ALL_TIERS = ["S+", "S", "A", "B", "C", "D"] as const;
export type TierNameValue = "S+" | "S" | "A" | "B" | "C" | "D";

export type TierGrade = (typeof ALL_TIERS)[number];

export const TIER_GRADE_STYLES = {
  "S+": {
    color: colors["accent-orange"]["700"],
    badgeBg: colors["tier-badge-bg"]["600"],
    cardClass: "border-t-accent-orange-700",
    badgeClass: "border-accent-orange-700 text-accent-orange-700 bg-tier-badge-bg-600",
  },
  S: {
    color: colors["accent-blue"]["400"],
    badgeBg: colors["tier-badge-bg"]["500"],
    cardClass: "border-t-accent-blue-400",
    badgeClass: "border-accent-blue-400 text-accent-blue-400 bg-tier-badge-bg-500",
  },
  A: {
    color: colors["accent-blue"]["200"],
    badgeBg: colors["tier-badge-bg"]["400"],
    cardClass: "border-t-accent-blue-200",
    badgeClass: "border-accent-blue-200 text-accent-blue-200 bg-tier-badge-bg-400",
  },
  B: {
    color: colors["white"],
    badgeBg: colors["tier-badge-bg"]["300"],
    cardClass: "border-t-white",
    badgeClass: "border-white text-white bg-tier-badge-bg-300",
  },
  C: {
    color: colors["accent-red"]["100"],
    badgeBg: colors["tier-badge-bg"]["200"],
    cardClass: "border-t-accent-red-100",
    badgeClass: "border-accent-red-100 text-accent-red-100 bg-tier-badge-bg-200",
  },
  D: {
    color: colors["accent-red"]["450"],
    badgeBg: colors["tier-badge-bg"]["100"],
    cardClass: "border-t-accent-red-450",
    badgeClass: "border-accent-red-450 text-accent-red-450 bg-tier-badge-bg-100",
  },
} satisfies Record<TierGrade, { color: string; badgeBg: string; cardClass: string; badgeClass: string }>;

export function getTierGradeColorHex(grade: string) {
  return (TIER_GRADE_STYLES[grade as TierGrade] ?? TIER_GRADE_STYLES.B).color;
}

export function getTierGradeBadgeBgHex(grade: string) {
  return (TIER_GRADE_STYLES[grade as TierGrade] ?? TIER_GRADE_STYLES.B).badgeBg;
}

export function getTierListPickRateCutoff(queueType?: string) {
  if (queueType === QueueTypeS.ONE_FOR_ALL) {
    return 0;
  }
  return 0.5;
}

export function getTier(winrate: number) {
  if (winrate < 45) {
    return "D";
  } else if (winrate < 48.5) {
    return "C";
  } else if (winrate < 51.5) {
    return "B";
  } else if (winrate < 53) {
    return "A";
  } else if (winrate < 55) {
    return "S";
  } else {
    return "S+";
  }
}

export function getTierColor(winrate: number) {
  if (isNaN(winrate)) {
    return "";
  }

  if (winrate < 45) {
    return "shinggo-tier";
  } else if (winrate < 48.5) {
    return "meh-tier";
  } else if (winrate < 51.5) {
    return "okay-tier";
  } else if (winrate < 53) {
    return "good-tier";
  } else if (winrate < 55) {
    return "great-tier";
  } else {
    return "volxd-tier";
  }
}

export function getTierColorHex(winrate: number, defaultColorHex = colors["white"]) {
  if (isNaN(winrate)) {
    return "";
  }

  if (winrate < 45) {
    return colors["accent-red"]["450"];
  } else if (winrate < 48.5) {
    return colors["accent-red"]["100"];
  } else if (winrate < 51.5) {
    return defaultColorHex;
  } else if (winrate < 53) {
    return colors["accent-blue"]["200"];
  } else if (winrate < 55) {
    return colors["accent-blue"]["400"];
  } else {
    return colors["accent-orange"]["700"];
  }
}

export function getLuTier(stdevs: number) {
  if (stdevs >= 2) {
    return "S+";
  } else if (stdevs >= 0.75) {
    return "S";
  } else if (stdevs >= 0) {
    return "A";
  } else if (stdevs >= -0.5) {
    return "B";
  } else if (stdevs >= -0.75) {
    return "C";
  } else {
    return "D";
  }
}

export function getLuTierColor(stdevs: number) {
  if (stdevs >= 2) {
    return "volxd-tier";
  } else if (stdevs >= 0.75) {
    return "great-tier";
  } else if (stdevs >= 0) {
    return "good-tier";
  } else if (stdevs >= -0.5) {
    return "okay-tier";
  } else if (stdevs >= -0.75) {
    return "meh-tier";
  } else {
    return "shinggo-tier";
  }
}

export function getLuTierColorHex(stdevs: number) {
  if (stdevs >= 2) {
    return colors["accent-orange"]["700"];
  } else if (stdevs >= 0.75) {
    return colors["accent-blue"]["400"];
  } else if (stdevs >= 0) {
    return colors["accent-blue"]["200"];
  } else if (stdevs >= -0.5) {
    return colors["white"];
  } else if (stdevs >= -0.75) {
    return colors["accent-red"]["100"];
  } else {
    return colors["accent-red"]["450"];
  }
}

// ─────────────────────────────────────────────────────────────────────
// ARAM Mayhem ranking-stats override — self-contained module
// ─────────────────────────────────────────────────────────────────────
//
// ARAM Mayhem has its own data pipeline (see data_tierlists-html.py
// in ugg-data-digest) that produces a static champion tier list JSON
// on the CDN, keyed on numeric champion keys, rather than flowing
// through the normal ranking-stats backend. This module reshapes
// that data source into the RankingStats layout the rest of the
// site's champion consumers expect, so anything that reads a
// champion's tier / stdevs / rank (e.g. ChampionProfileHeader's
// badge) works unchanged under the ARAM Mayhem queue type.
//
// This hook is only intended to be called via the top-level
// dispatcher in ranking-stats.ts — direct callers would have to
// re-implement the queueType check themselves. The dispatcher
// gates on validatedQueryParams.queueType === QueueTypeS.ARAM_MAYHEM
// and skips this hook otherwise.

// Reverse-map from the tier string in the ARAM Mayhem JSON back to
// a stdevs value that will round-trip cleanly through getLuTier()
// in @ugg/shared/utils/tier-helpers. Values are chosen as the
// MIDPOINT of each bucket rather than the bucket floor for two
// reasons:
//   • "A" tier's floor is exactly 0, which the header treats as
//     falsy in `stdevs && rank && getLuTier(stdevs)` — hitting the
//     floor would silently suppress the badge.
//   • Any downstream code that applies its own tie-breaking around
//     bucket boundaries can nudge a champion into a neighboring
//     tier if we hand it a boundary value; midpoints buy safety
//     margin cheaply.
// "D" tier is open-ended below -0.75 so -1 is arbitrary but well
// clear of the boundary.
export const ARAM_MAYHEM_TIER_TO_STDEVS: Record<"S+" | "S" | "A" | "B" | "C" | "D", number> = {
  "S+": 2.5,
  S: 1.375,
  A: 0.375,
  B: -0.25,
  C: -0.625,
  D: -1,
};

//--------------------------------------------------------------------------------
// Drew(TODO): We're creating some styling technical debt due to AI code, but
// we'll fix this later and integrate it better post-launch.

// Per-tier solid background class. Used in three places per card:
// the 4px left border (via the matching `border-[#X]` variant in
// TIER_BANNER_CLASSES), the 1px horizontal divider between banner
// and body, and the small vertical accent bar that sits just left
// of the tier letter inside the banner. Kept as a const map (rather
// than computed) so Tailwind's content scanner picks up the literal
// class strings at build time.
export const ARAM_MAYHEM_TIER_LABEL_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#FF9B00]",
  S: "bg-[#3273FA]",
  A: "bg-[#7EA4F4]",
  B: "bg-[#CDDCFE]",
  C: "bg-[#FCB1B2]",
  D: "bg-[#FF4E50]",
};

// Banner styling per tier — combines the border color (applied as
// 4px left border on banner and body, also as the matching divider
// 1px line color via TIER_LABEL_BG) with the gradient start color.
// Full class strings (not template-string interpolations) so
// Tailwind's JIT content scanner picks them up at build time.
//
// The gradient is `bg-gradient-to-r from-[#X]/<opacity> to-transparent`,
// applied to a banner that already has `bg-[#191937]` underneath —
// the tier color fades horizontally from 30% opacity at the left
// to fully transparent at the right, revealing the dark banner
// base. Opacity is 30% for every tier except B, where /20 is used
// because the much lighter #CDDCFE would otherwise overpower the
// gradient.
export const ARAM_MAYHEM_TIER_BANNER_CLASSES: Record<TierNameValue, string> = {
  "S+": "border-[#FF9B00] from-[#FF9B00]/30",
  S: "border-[#3273FA] from-[#3273FA]/30",
  A: "border-[#7EA4F4] from-[#7EA4F4]/30",
  B: "border-[#CDDCFE] from-[#CDDCFE]/20",
  C: "border-[#FCB1B2] from-[#FCB1B2]/30",
  D: "border-[#FF4E50] from-[#FF4E50]/30",
};

// Body background color per tier — alternates between the lighter
// (#191937) and darker (#11112A) navy shades to zebra-stripe the
// stacked tier cards. Matches the Figma spec exactly: S+/A/C use
// the lighter shade, S/B/D use the darker. The lighter color also
// matches the banner background, so for the "lighter" tiers
// (S+/A/C) the banner and body share a single background and the
// 1px tier-color divider is the only visual separator between
// them; for the "darker" tiers (S/B/D) the banner sits visibly
// brighter than the body.
export const ARAM_MAYHEM_TIER_BODY_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#191937]",
  S: "bg-[#11112A]",
  A: "bg-[#191937]",
  B: "bg-[#11112A]",
  C: "bg-[#191937]",
  D: "bg-[#11112A]",
};

//--------------------------------------------------------------------------------
